import { Component, computed, inject, LOCALE_ID, signal, DestroyRef, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterInput } from '../../../../shared/ui/shared-filters/filter-input/filter-input';
import { displayName, optionalName } from '../../../../validators/name-rules';
import { arabicOnlyValidator, englishOnlyValidator } from '../city.validators';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { Loading } from '../../../../utils/loading/loading';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { CityError, Governorate } from '../../../../pages/admin-dashboard/cities/city.model';
import { CityService } from '../../../../services/city/city.service';
import { DropdownArrow } from '../../../../shared/ui/icons/dropdown-arrow/dropdown-arrow';

@Component({
  selector: 'app-create-city-component',
  standalone: true,
  imports: [
    FilterInput,
    ReactiveFormsModule,
    ConfirmationDialog,
    SuccessfulNotification,
    Loading,
    SubmitButton,
    HasScopeDirective,
    DropdownArrow
],
  templateUrl: './create-city-component.html',
  styleUrl: './create-city-component.scss',
})
export class CreateCityComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private locale = inject(LOCALE_ID);
  private cityService = inject(CityService);
  private destroyRef = inject(DestroyRef);

  readonly SCOPES = SCOPES;

  isArabic = computed(() => this.locale.startsWith('ar'));
  isLoading = signal(false);

  governorates = signal<Governorate[]>([]);

titleArLabel = $localize`:@@cityTitleArabic:City Name (Arabic)`;
titleArPlaceholder = $localize`:@@cityTitleArabicPlaceholder:Enter city name in Arabic`;

titleEnLabel = $localize`:@@cityTitleEnglish:City Name (English)`;
titleEnPlaceholder = $localize`:@@cityTitleEnglishPlaceholder:Enter city name in English (optional)`;

governorateLabel = $localize`:@@governorateLabel:Governorate`;
governoratePlaceholder = $localize`:@@governoratePlaceholder:Select Governorate`;

cancelButton = $localize`:@@cityCancel:Cancel`;
submitButton = $localize`:@@citySubmit:Save`;
loadingText = $localize`:@@cityLoading:Loading...`;

showConfirmation = signal(false);
confirmationTitle = $localize`:@@cityCreateConfirmationTitle:Confirm Addition`;
confirmationDescription = $localize`:@@cityCreateConfirmation:Are you sure you want to add this city?`;
confirmButtonText = signal($localize`:@@cityConfirm:Confirm`);
cityExistsMessage = $localize`:@@cityExistsMessage:exists in`;

  cityForm = new FormGroup({
    titleAr: new FormControl<string | null>(null, [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(100),
      arabicOnlyValidator(),
    ]),

    titleEn: new FormControl('', [
      Validators.minLength(2),
      Validators.maxLength(100),
      englishOnlyValidator(),
    ]),

    governorateId: new FormControl<number | null>(null, [Validators.required]),
  });

  ngOnInit() {
    this.loadGovernorates();
  }

  governoratesLoading = signal(false);
  governoratesError = signal('');

  loadGovernorates() {
    this.governoratesLoading.set(true);
    this.governoratesError.set('');

    const sub = this.cityService.getGovernorates().subscribe({
      next: (res) => {
        this.governorates.set(res.list || []);
        this.governoratesLoading.set(false);
      },
      error: (error: CityError) => {
        this.governoratesLoading.set(false);

        this.governoratesError.set(
          this.isArabic()
            ? error?.messageAr || 'حدث خطأ أثناء تحميل المحافظات'
            : error?.messageEn || 'An error occurred while loading governorates',
        );
      },
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  readonly titleArControl = computed(() => this.cityForm.controls.titleAr);
  readonly titleEnControl = computed(() => this.cityForm.controls.titleEn);
  readonly governorateIdControl = computed(() => this.cityForm.controls.governorateId);

  onArabicChange(value: string | number | null) {
    this.titleArControl().setValue(value?.toString() ?? null);
    this.titleArControl().markAsTouched();
    this.titleArControl().updateValueAndValidity();
  }

  onEnglishChange(value: string | number | null) {
    this.titleEnControl().setValue(value?.toString() ?? '');
    this.titleEnControl().markAsTouched();
    this.titleEnControl().updateValueAndValidity();
  }

  submitForm() {
    this.errorMessage = signal('');
    this.cityForm.markAllAsTouched();
    if (this.cityForm.invalid) {
      return;
    }
    this.showConfirmation.set(true);
  }

  private goToCitiesList() {
    const governorateId = this.route.parent?.snapshot.paramMap.get('id');
    this.router.navigate(governorateId ? ['/admin/cities', governorateId] : ['/admin/governorates']);
  }

  cancel() {
    this.goToCitiesList();
  }

  successMessage = signal<string | null>(null);
  errorMessage = signal('');

  confirmCreate() {
    this.isLoading.set(true);
    this.confirmButtonText.set(this.loadingText);
    this.errorMessage = signal('');

    const data = this.cityForm.getRawValue();
    
    const dto = {
      titleAr: data.titleAr!.trim(),
      titleEn: optionalName(data.titleEn),
      governorateId: Number(data.governorateId),
    };
const selectedGovernorate = this.governorates().find(
  (gov) => gov.id === dto.governorateId,
);

const governorateName = selectedGovernorate
  ? this.isArabic()
    ? selectedGovernorate.titleAr
    : selectedGovernorate.titleEn
  : '';

    const subscription = this.cityService.createCity(dto).subscribe({
      next: () => {
        this.isLoading.set(false);
       this.confirmButtonText.set($localize`:@@cityConfirm:Confirm`);
        this.showConfirmation.set(false);
        this.errorMessage.set('');

        const cityName = displayName(this.isArabic(), dto.titleAr, dto.titleEn);

        this.successMessage.set(
          `${$localize`:@@cityCreatedSuccessfully:City Created `} "${cityName}" ${$localize`:@@cityCreatedSuccessfully2:successfully`}`,
        );

        setTimeout(() => {
          this.goToCitiesList();
        }, 3000);
      },

      error: (error: CityError) => {
      console.log('CITY ERROR:', error);
        this.isLoading.set(false);
        this.confirmButtonText.set($localize`:@@cityConfirm:تأكيد`);

        if ( error.reqBodyErrors) {
          const hasArabicError = error.reqBodyErrors.some((err) => err.includes('Field: titleAr'));
          const hasEnglishError = error.reqBodyErrors.some((err) => err.includes('Field: titleEn'));

          if (hasArabicError && hasEnglishError) {
            this.errorMessage.set(
              this.isArabic()
                ? 'اسم المدينة باللغة العربية والإنجليزية غير صالح.'
                : 'The Arabic and English city names are invalid.',
            );
            return;
          }

          if (hasArabicError) {
            this.errorMessage.set(
              this.isArabic() ? 'اسم المدينة باللغة العربية غير صالح.' : 'The Arabic city name is invalid.',
            );
            return;
          }

          if (hasEnglishError) {
            this.errorMessage.set(
              this.isArabic() ? 'اسم المدينة باللغة الإنجليزية غير صالح.' : 'The English city name is invalid.',
            );
            return;
          }
        }
   if (error.code === 'CITY-0001') {
  const cityName = displayName(this.isArabic(), dto.titleAr, dto.titleEn);

  this.errorMessage.set(
    `"${cityName}" ${this.cityExistsMessage} ${governorateName}`,
  );

  return;
}



        this.errorMessage.set(
          this.isArabic()
            ? error.messageAr || error.messageEn || 'حدث خطأ غير متوقع'
            : error.messageEn || error.messageAr || 'An unexpected error occurred',
        );
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }


  closeConfirmation() {
    this.showConfirmation.set(false);
  }
}
